import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function ArticleVancouverSkiing() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "skiing-from-vancouver";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext();
  const ref_data = articles.find((art) => art.slug === ref_id);

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "section-1": "Whistler Blackcomb",
    "section-2": "Cypress Mountain",
    "section-3": "Grouse Mountain",
    "section-4": "Mount Seymour",
    "section-5": "Sasquatch Mountain Resort",
    "section-6": "Manning Park Resort",
    "section-7": "Garibaldi Backcountry",
    "section-8": "Duffey Lake Backcountry",
    "section-9": "Mount Baker Ski Area",
    "section-10": "Big White Ski Resort",
    "local-regulations": "Local Regulations",
    "faq": "FAQ",
    "conclusion": "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  const referencesData = [
    {
      url: "https://avalanche.ca/",
      label: "Avalanche Canada",
    },
    {
      url: "https://www.drivebc.ca/",
      label: "DriveBC Road Conditions",
    },
    {
      url: "https://www.whistlerblackcomb.com/",
      label: "Whistler Blackcomb Official Website",
    },
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  // Each location's "id" corresponds to the relevant section or marker ref.
  const locations = [
    {
      id: "section-1", // Whistler Blackcomb
      lat: 50.10833,
      lng: -122.94250,
      popupText: "Whistler Blackcomb",
    },
    {
      id: "section-2", // Cypress Mountain
      lat: 49.39556,
      lng: -123.20333,
      popupText: "Cypress Mountain",
    },
    {
      id: "section-3", // Grouse Mountain
      lat: 49.37956,
      lng: -123.08180,
      popupText: "Grouse Mountain",
    },
    {
      id: "section-4", // Mount Seymour
      lat: 49.39611,
      lng: -122.94444,
      popupText: "Mount Seymour",
    },
    {
      id: "section-5", // Sasquatch
      lat: 49.37694,
      lng: -121.93500,
      popupText: "Sasquatch Mountain Resort (Hemlock)",
    },
    {
      id: "section-6", // Manning Park
      lat: 49.06833,
      lng: -120.91639,
      popupText: "Manning Park Resort",
    },
    {
      id: "section-7", // Garibaldi Backcountry
      lat: 49.78556,
      lng: -122.98889,
      popupText: "Garibaldi Provincial Park Backcountry (Elfin Lakes)",
    },
    {
      id: "section-8", // Duffey Lake & Joffre
      lat: 50.34722,
      lng: -122.47667,
      popupText: "Duffey Lake & Joffre Lakes Backcountry",
    },
    {
      id: "section-9", // Mt. Baker
      lat: 48.86200,
      lng: -121.65400,
      popupText: "Mount Baker Ski Area",
    },
    {
      id: "section-10", // Big White
      lat: 49.72194,
      lng: -118.92889,
      popupText: "Big White Ski Resort",
    },
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>Skiing from Vancouver – Top Resorts, Tips & Costs</title>
        <meta
          name="description"
          content="A practical, in-depth guide on skiing near Vancouver: top resorts, budgets, tips, and more."
        />
        <meta
          name="keywords"
          content="Vancouver skiing, Whistler Blackcomb, Cypress Mountain, Grouse Mountain, ski resorts near Vancouver"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/skiing-from-vancouver.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="Skiing from Vancouver – Top Resorts, Tips & Costs"
        />
        <meta
          property="og:description"
          content="A comprehensive guide for anyone looking to hit the slopes around Vancouver."
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/skiing-from-vancouver.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/skiing-from-vancouver.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Skiing from Vancouver – Top Resorts, Tips & Costs"
        />
        <meta
          name="twitter:description"
          content="Learn about the best ski resorts near Vancouver, their coordinates, approximate costs, and more."
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/skiing-from-vancouver.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={
                ref_data?.image ||
                "https://bcouver.com/assets/images/article-fallback.jpg"
              }
              alt="Skiing in Vancouver"
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia />

            {/* SECTION: Introduction */}
            <h2 id="intro" ref={(el) => setSpotRef("intro", el)}>
              Introduction
            </h2>
            <p>
              Vancouver, British Columbia is a skier’s paradise, offering
              unparalleled access to both urban amenities and towering coastal
              mountains. Within minutes to a few short hours, you can reach
              world-class ski resorts like Whistler Blackcomb or explore
              breathtaking backcountry zones in Garibaldi Provincial Park.
              Whether you're a beginner looking for a quick day trip or an
              advanced rider seeking deep powder and backcountry thrills, this
              guide provides the key details: from location coordinates to
              approximate costs, operating hours, and local regulations. Read on
              to plan your next alpine adventure!
            </p>

            {/* SECTION: Whistler Blackcomb */}
            <h2 id="section-1" ref={(el) => setSpotRef("section-1", el)}>
              Whistler Blackcomb
              <Marker
                coordY={50.10833}
                coordX={-122.94250}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </h2>
            <p>
              <strong>Coordinates:</strong> 50.10833, -122.94250
            </p>
            <p>
              Whistler Blackcomb is North America’s largest ski resort, featuring
              over 200 runs across two massive mountains. Located about two
              hours north of Vancouver, it’s a favorite for international
              visitors and a frequent weekend destination for locals. Expect
              lift tickets around $120–$180 CAD (depending on season or advance
              purchase), extensive on-site amenities, and a bustling
              pedestrian-friendly village. Although it can be crowded, Whistler
              offers an unmatched variety of terrain, from gentle greens to
              expert bowls, plus the spectacular PEAK 2 PEAK Gondola spanning
              the alpine ridges.
            </p>

            {/* SECTION: Cypress Mountain */}
            <h2 id="section-2" ref={(el) => setSpotRef("section-2", el)}>
              Cypress Mountain
              <Marker
                coordY={49.39556}
                coordX={-123.20333}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </h2>
            <p>
              <strong>Coordinates:</strong> 49.39556, -123.20333
            </p>
            <p>
              Cypress Mountain is only about 30 minutes from downtown Vancouver,
              making it one of the most convenient ski hills for a quick escape.
              Day tickets range from $70–$95 CAD, with night-skiing options
              starting around $60. As the largest of Vancouver’s North Shore
              mountains, Cypress has 53 runs, expansive night skiing terrain,
              and winter activities like tubing and snowshoeing. With beautiful
              views overlooking the city and Howe Sound, it’s a local favorite
              for after-work ski sessions.
            </p>

            {/* SECTION: Grouse Mountain */}
            <h2 id="section-3" ref={(el) => setSpotRef("section-3", el)}>
              Grouse Mountain
              <Marker
                coordY={49.37956}
                coordX={-123.08180}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </h2>
            <p>
              <strong>Coordinates:</strong> 49.37956, -123.08180
            </p>
            <p>
              Often called “The Peak of Vancouver,” Grouse Mountain combines
              skiing, sightseeing, and year-round attractions. Visitors take the
              Skyride gondola to reach the summit where 33 runs await, plus a
              lively mountaintop area with dining and entertainment. Tickets
              (including gondola access) can be around $80–$100 CAD, with lower
              rates for night skiing. Convenient public transit from downtown
              makes Grouse the easiest mountain to reach without a car.
            </p>

            {/* SECTION: Mount Seymour */}
            <h2 id="section-4" ref={(el) => setSpotRef("section-4", el)}>
              Mount Seymour
              <Marker
                coordY={49.39611}
                coordX={-122.94444}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </h2>
            <p>
              <strong>Coordinates:</strong> 49.39611, -122.94444
            </p>
            <p>
              Mount Seymour is celebrated for its friendly, family-run vibe,
              fantastic terrain parks, and abundant snowfall. It offers about 23
              marked runs and night skiing on many of them. Adult tickets range
              from $60–$85 CAD, with shuttle service from North Vancouver
              available. Beginners appreciate Seymour’s gentle slopes and budget
              options, while freestyle enthusiasts enjoy the park features and
              tree runs.
            </p>

            {/* SECTION: Sasquatch Mountain Resort */}
            <h2 id="section-5" ref={(el) => setSpotRef("section-5", el)}>
              Sasquatch Mountain Resort (Hemlock Valley)
              <Marker
                coordY={49.37694}
                coordX={-121.93500}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </h2>
            <p>
              <strong>Coordinates:</strong> 49.37694, -121.93500
            </p>
            <p>
              Formerly Hemlock Valley, Sasquatch Mountain is about 2.5 hours east
              of Vancouver. It’s smaller than the North Shore hills, but lift
              tickets around $60–$70 CAD and short lift lines offer a refreshing
              break from busier resorts. It boasts about 34 runs across 300
              acres, and good snowfall. A small lodge area and limited on-mountain
              lodging mean many visitors come just for the day, but it’s a
              hidden gem for powder seekers.
            </p>

            {/* SECTION: Manning Park Resort */}
            <h2 id="section-6" ref={(el) => setSpotRef("section-6", el)}>
              Manning Park Resort (Gibson Pass)
              <Marker
                coordY={49.06833}
                coordX={-120.91639}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </h2>
            <p>
              <strong>Coordinates:</strong> 49.06833, -120.91639
            </p>
            <p>
              Manning Park, roughly three hours east of Vancouver, features a
              modest downhill area plus extensive Nordic trails. Tickets hover
              around $50–$70 CAD, with on-site lodging at Manning Park Resort
              making it an appealing weekend getaway. Visitors enjoy the
              tranquility of a provincial park setting, often finding fewer
              crowds and serene powder days.
            </p>

            {/* SECTION: Garibaldi Backcountry */}
            <h2 id="section-7" ref={(el) => setSpotRef("section-7", el)}>
              Garibaldi Provincial Park Backcountry (Diamond Head/Elfin Lakes)
              <Marker
                coordY={49.78556}
                coordX={-122.98889}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </h2>
            <p>
              <strong>Coordinates:</strong> 49.78556, -122.98889
            </p>
            <p>
              Garibaldi’s Diamond Head area (near Elfin Lakes) offers
              backcountry ski touring amid stunning alpine scenery. No lift
              ticket required, but avalanche gear, knowledge, and a reservation
              if staying in Elfin Lakes Hut are essential. Always check weather
              and avalanche forecasts (via Avalanche Canada) before heading
              out. This area is a top choice for intermediate to advanced
              backcountry enthusiasts seeking untracked snow and multi-day
              tours.
            </p>

            {/* SECTION: Duffey Lake Backcountry */}
            <h2 id="section-8" ref={(el) => setSpotRef("section-8", el)}>
              Duffey Lake & Joffre Lakes Backcountry
              <Marker
                coordY={50.34722}
                coordX={-122.47667}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </h2>
            <p>
              <strong>Coordinates:</strong> 50.34722, -122.47667
            </p>
            <p>
              The Duffey Lake corridor and Joffre Lakes area near Pemberton have
              become legendary for their easily accessible, high-quality
              backcountry ski terrain. Expect dry interior-like powder and
              diverse routes—from mellow tree skiing to challenging alpine
              objectives. Proper avalanche training and equipment are mandatory
              in this unmanaged wilderness. Parking can be limited on busy
              weekends; always check road and weather conditions before you go.
            </p>

            {/* SECTION: Mount Baker Ski Area */}
            <h2 id="section-9" ref={(el) => setSpotRef("section-9", el)}>
              Mount Baker Ski Area (Washington, USA)
              <Marker
                coordY={48.86200}
                coordX={-121.65400}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </h2>
            <p>
              <strong>Coordinates:</strong> 48.86200, -121.65400
            </p>
            <p>
              A 2.5-hour drive from Vancouver (plus a border crossing), Mount
              Baker boasts some of the deepest snowfall in the world. Lift
              tickets range around $75–$90 USD, significantly cheaper than many
              major resorts. While smaller in acreage than Whistler, the powder
              quality and laid-back vibe are huge draws. No on-mountain lodging
              means day trips or staying in nearby towns like Glacier or
              Bellingham. Don’t forget to bring your passport for the
              Canada–US border!
            </p>

            {/* SECTION: Big White Ski Resort */}
            <h2 id="section-10" ref={(el) => setSpotRef("section-10", el)}>
              Big White Ski Resort (Okanagan)
              <Marker
                coordY={49.72194}
                coordX={-118.92889}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </h2>
            <p>
              <strong>Coordinates:</strong> 49.72194, -118.92889
            </p>
            <p>
              About a five-hour drive from Vancouver (or a short flight to
              Kelowna), Big White is renowned for its family-friendly
              ski-in/ski-out village and dry, “champagne powder.” Lift tickets
              average around $110–$130 CAD. While it's a trek from the city, the
              lighter interior snow, extensive terrain, and bustling village
              life make Big White a worthy multi-day destination.
            </p>

            {/* SECTION: Local Regulations */}
            <h2 id="local-regulations" ref={(el) => setSpotRef("local-regulations", el)}>
              Local Regulations & Safety
            </h2>
            <p>
              Driving to these mountains typically requires winter tires or
              chains (by law) from October to April. Stick to in-bounds areas at
              resorts unless you have proper avalanche gear and training. BC’s
              Alpine Responsibility Code applies at all resorts, emphasizing
              safe, controlled skiing and respect for posted signs. For
              backcountry trips, always check avalanche forecasts and carry
              safety equipment.
            </p>
            <p>
              Remember that different resorts may have unique policies,
              including reservation systems (Mount Seymour often uses 4-hour
              time slots on busy days), parking fees (e.g., Whistler Village
              lots), or pass requirements.
            </p>

            {/* SECTION: FAQ */}
            <h2 id="faq" ref={(el) => setSpotRef("faq", el)}>
              FAQ
            </h2>
            <div className="faq-section">
              <h3>1. When is the best time to ski near Vancouver?</h3>
              <p>
                Generally, late December through March offers the most reliable
                snow. January and February are peak months for snowfall, though
                March can provide excellent spring skiing and fewer crowds.
              </p>

              <h3>2. Which resort is best for beginners?</h3>
              <p>
                Mount Seymour is a local favorite for beginners due to its
                smaller size and gentler slopes. Whistler Blackcomb also has
                world-class beginner facilities, but it’s pricier and farther
                from Vancouver.
              </p>

              <h3>3. Are day trips to Whistler feasible?</h3>
              <p>
                Yes. Whistler is roughly two hours away. Many people leave early
                in the morning and return after lifts close, although it can be
                a long day. Shuttle buses from downtown Vancouver are available
                if you don’t want to drive.
              </p>
            </div>

            {/* SECTION: Conclusion */}
            <h2 id="conclusion" ref={(el) => setSpotRef("conclusion", el)}>
              Conclusion
            </h2>
            <p>
              Whether you crave the convenience of Cypress and Grouse or the
              epic scale of Whistler Blackcomb, Vancouver’s proximity to so many
              resorts makes it a premier destination for winter-sports
              enthusiasts. With proper planning—checking road conditions,
              budgeting lift ticket costs, and following safety guidelines—you
              can tailor a ski trip to any skill level or preference. Embrace
              the winter wonderland just beyond the city limits and enjoy
              Vancouver’s renowned ski scene. Always remember to check official
              resources and travel with safety in mind. See you on the slopes!
            </p>

            {/* BOTTOM COMPONENT: references, author, related articles, etc. */}
            <Bottom referencesData={referencesData} ref_data={ref_data} />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay ref={mobileMapRef} markers={locations} spotRefs={spotRefs} />

      <Footer />
    </>
  );
}